import { useState, useEffect, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Heart, LogOut, Mail, Coins, ArrowLeft, MailOpen, RefreshCw, MessageSquareQuote, BarChart3, Repeat, Download, Settings as Cog, AlertTriangle, Globe, Users, Activity, FileText, UserCircle, Newspaper, Map, Target } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import api, { formatApiError, API_BASE } from "../lib/api";
import TestimonialsManager from "../components/TestimonialsManager";
import Analytics from "../components/Analytics";
import SubscriptionsManager from "../components/SubscriptionsManager";
import PaymentFailures from "../components/PaymentFailures";
import SettingsManager from "../components/SettingsManager";
import UsersManager from "../components/UsersManager";
import AuditLogs from "../components/AuditLogs";
import EmailTemplatesManager from "../components/EmailTemplatesManager";
import BoardManager from "../components/BoardManager";
import BlogManager from "../components/BlogManager";
import ImpactMapManager from "../components/ImpactMapManager";
import CampaignsManager from "../components/CampaignsManager";

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const { t, lang, toggleLang } = useLanguage();
  const navigate = useNavigate();
  const [tab, setTab] = useState("donations");
  const [donations, setDonations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  const role = user?.role || "viewer";
  const isAdmin = role === "admin";

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [d, m] = await Promise.all([api.get("/donations"), api.get("/contact")]);
      setDonations(d.data || []);
      setMessages(m.data || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stats = useMemo(() => {
    const total = donations.reduce((s, d) => s + (d.amount || 0), 0);
    return { total, count: donations.length, unread: messages.filter((m) => !m.read).length };
  }, [donations, messages]);

  const handleLogout = () => {
    logout();
    navigate("/admin/login");
  };

  const markRead = async (id) => {
    try {
      await api.patch(`/contact/${id}/read`);
      setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, read: true } : m)));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const exportCsv = async () => {
    const token = localStorage.getItem("fcs_token");
    try {
      const res = await fetch(`${API_BASE}/admin/donations.csv`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const cd = res.headers.get("content-disposition") || "";
      const m = /filename="([^"]+)"/.exec(cd);
      a.download = m ? m[1] : "dons.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Export CSV téléchargé");
    } catch (_) {
      toast.error("Échec de l'export");
    }
  };

  return (
    <main className="min-h-screen bg-[#F3F0EA]" data-testid="admin-dashboard">
      <header className="bg-white border-b border-black/5 sticky top-0 z-30">
        <div className="container-narrow flex items-center justify-between h-16">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-[#002B49] flex items-center justify-center">
              <Heart size={16} fill="#C5A059" className="text-[#C5A059]" />
            </div>
            <div>
              <div className="font-serif font-semibold text-[#0A192F]">{t.admin.dashboardTitle}</div>
              <div className="text-[10px] uppercase tracking-wider text-[#64748B]">
                {user?.email}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={toggleLang}
              className="hidden sm:inline-flex items-center gap-1.5 text-sm font-medium px-3 py-2 rounded-md text-[#64748B] hover:bg-black/5"
              data-testid="admin-lang-toggle"
              aria-label="Switch language"
            >
              <Globe size={14} /> {lang.toUpperCase()}
            </button>
            <Link
              to="/"
              className="text-sm text-[#64748B] hover:text-[#002B49] hidden sm:inline-flex items-center gap-1.5 px-3 py-2 rounded-md hover:bg-black/5"
              data-testid="dashboard-back-home"
            >
              <ArrowLeft size={14} /> {t.admin.backHome}
            </Link>
            <button
              onClick={fetchAll}
              className="p-2 rounded-md hover:bg-black/5 text-[#64748B]"
              aria-label="refresh"
              data-testid="dashboard-refresh"
            >
              <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
            </button>
            <button
              onClick={handleLogout}
              className="text-sm inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#002B49] text-white hover:bg-[#001F35]"
              data-testid="logout-btn"
            >
              <LogOut size={14} /> {t.admin.logout}
            </button>
          </div>
        </div>
      </header>

      <div className="container-narrow py-10">
        {/* KPIs */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
          <KpiCard label={t.admin.totalRaised} value={`${stats.total.toFixed(2)} €`} Icon={Coins} testid="kpi-total" />
          <KpiCard label={t.admin.totalDonations} value={stats.count} Icon={Heart} testid="kpi-count" />
          <KpiCard label={t.admin.unreadMessages} value={stats.unread} Icon={Mail} testid="kpi-unread" />
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-white rounded-lg p-1 border border-black/5 w-fit mb-6 flex-wrap">
          <TabBtn active={tab === "donations"} onClick={() => setTab("donations")} testid="tab-donations">
            <Coins size={14} className="mr-2" />
            {t.admin.donationsTab} ({donations.length})
          </TabBtn>
          <TabBtn active={tab === "subscriptions"} onClick={() => setTab("subscriptions")} testid="tab-subscriptions">
            <Repeat size={14} className="mr-2" />
            {t.admin.subscriptionsTab}
          </TabBtn>
          <TabBtn active={tab === "failures"} onClick={() => setTab("failures")} testid="tab-failures">
            <AlertTriangle size={14} className="mr-2" />
            {t.admin.failuresTab}
          </TabBtn>
          <TabBtn active={tab === "messages"} onClick={() => setTab("messages")} testid="tab-messages">
            <Mail size={14} className="mr-2" />
            {t.admin.messagesTab} ({messages.length})
          </TabBtn>
          <TabBtn active={tab === "testimonials"} onClick={() => setTab("testimonials")} testid="tab-testimonials">
            <MessageSquareQuote size={14} className="mr-2" />
            {t.admin.testimonialsTab}
          </TabBtn>
          <TabBtn active={tab === "analytics"} onClick={() => setTab("analytics")} testid="tab-analytics">
            <BarChart3 size={14} className="mr-2" />
            {t.admin.analyticsTab}
          </TabBtn>
          <TabBtn active={tab === "settings"} onClick={() => setTab("settings")} testid="tab-settings">
            <Cog size={14} className="mr-2" />
            {t.admin.settingsTab}
          </TabBtn>
          <TabBtn active={tab === "board"} onClick={() => setTab("board")} testid="tab-board">
            <UserCircle size={14} className="mr-2" />
            {t.admin.boardTab}
          </TabBtn>
          <TabBtn active={tab === "blog"} onClick={() => setTab("blog")} testid="tab-blog">
            <Newspaper size={14} className="mr-2" />
            {t.admin.blogTab}
          </TabBtn>
          <TabBtn active={tab === "impactMap"} onClick={() => setTab("impactMap")} testid="tab-impact-map">
            <Map size={14} className="mr-2" />
            {t.admin.impactMapTab}
          </TabBtn>
          <TabBtn active={tab === "campaigns"} onClick={() => setTab("campaigns")} testid="tab-campaigns">
            <Target size={14} className="mr-2" />
            {t.admin.campaignsTab}
          </TabBtn>
          {isAdmin && (
            <>
              <TabBtn active={tab === "users"} onClick={() => setTab("users")} testid="tab-users">
                <Users size={14} className="mr-2" />
                {t.admin.usersTab}
              </TabBtn>
              <TabBtn active={tab === "audit"} onClick={() => setTab("audit")} testid="tab-audit">
                <Activity size={14} className="mr-2" />
                {t.admin.auditTab}
              </TabBtn>
              <TabBtn active={tab === "templates"} onClick={() => setTab("templates")} testid="tab-templates">
                <FileText size={14} className="mr-2" />
                {t.admin.templatesTab}
              </TabBtn>
            </>
          )}
        </div>

        {tab === "subscriptions" && <SubscriptionsManager />}
        {tab === "failures" && <PaymentFailures tAdmin={t.admin} />}
        {tab === "settings" && <SettingsManager tAdmin={t.admin} />}
        {tab === "board" && <BoardManager tAdmin={t.admin} />}
        {tab === "blog" && <BlogManager tAdmin={t.admin} />}
        {tab === "impactMap" && <ImpactMapManager tAdmin={t.admin} />}
        {tab === "campaigns" && <CampaignsManager tAdmin={t.admin} lang={lang} />}
        {tab === "analytics" && <Analytics />}
        {tab === "testimonials" && <TestimonialsManager />}
        {tab === "users" && isAdmin && <UsersManager tAdmin={t.admin} lang={lang} />}
        {tab === "audit" && isAdmin && <AuditLogs tAdmin={t.admin} lang={lang} />}
        {tab === "templates" && isAdmin && <EmailTemplatesManager tAdmin={t.admin} />}

        {tab === "donations" && (
          <div data-testid="donations-section">
            <div className="flex justify-end mb-3">
              <button
                onClick={exportCsv}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-[#002B49] text-white hover:bg-[#001F35] text-sm font-medium"
                data-testid="export-csv-btn"
              >
                <Download size={14} /> {t.admin.exportCsv}
              </button>
            </div>

            <div className="bg-white rounded-2xl border border-black/5 overflow-hidden" data-testid="donations-table">
            {loading ? (
              <div className="p-8 text-center text-[#64748B]">...</div>
            ) : donations.length === 0 ? (
              <div className="p-12 text-center text-[#64748B]" data-testid="no-donations">
                {t.admin.noDonations}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#FDFBF7] border-b border-black/5">
                    <tr className="text-left text-[#64748B] uppercase text-[11px] tracking-wider">
                      <th className="px-6 py-3 font-medium">{t.admin.colDate}</th>
                      <th className="px-6 py-3 font-medium">{t.admin.colDonor}</th>
                      <th className="px-6 py-3 font-medium">{t.admin.colEmail}</th>
                      <th className="px-6 py-3 font-medium">{t.admin.colAmount}</th>
                      <th className="px-6 py-3 font-medium">{t.admin.colType}</th>
                      <th className="px-6 py-3 font-medium">{t.admin.colPaypal}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {donations.map((d, i) => (
                      <tr key={d.id} className="border-b border-black/5 last:border-0 hover:bg-[#FDFBF7]" data-testid={`donation-row-${i}`}>
                        <td className="px-6 py-4 text-[#64748B] whitespace-nowrap">
                          {new Date(d.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 font-medium text-[#0A192F]">{d.donor_name}</td>
                        <td className="px-6 py-4 text-[#334155]">{d.donor_email}</td>
                        <td className="px-6 py-4 font-serif text-lg font-semibold text-[#002B49]">
                          {d.amount.toFixed(2)} €
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-block px-2.5 py-0.5 rounded-full text-[11px] font-medium ${
                            d.donation_type === "monthly" ? "bg-blue-100 text-blue-800" : "bg-emerald-100 text-emerald-800"
                          }`}>
                            {d.donation_type === "monthly" ? t.admin.typeMonthly : t.admin.typeOneTime}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-[11px] text-[#64748B] font-mono truncate max-w-[160px]">
                          {d.paypal_order_id || "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            </div>
          </div>
        )}

        {tab === "messages" && (
          <div className="space-y-3" data-testid="messages-list">
            {loading ? (
              <div className="bg-white rounded-2xl p-8 text-center text-[#64748B]">Chargement...</div>
            ) : messages.length === 0 ? (
              <div className="bg-white rounded-2xl p-12 text-center text-[#64748B]" data-testid="no-messages">
                {t.admin.noMessages}
              </div>
            ) : (
              messages.map((m, i) => (
                <div
                  key={m.id}
                  className={`bg-white rounded-xl border p-5 ${m.read ? "border-black/5" : "border-[#C5A059]"}`}
                  data-testid={`message-row-${i}`}
                >
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-[#0A192F]">{m.name}</span>
                        <span className="text-[#64748B] text-sm">&lt;{m.email}&gt;</span>
                        {!m.read && (
                          <span className="text-[10px] bg-[#C5A059] text-white px-2 py-0.5 rounded-full uppercase tracking-wider">
                            {t.admin.unread}
                          </span>
                        )}
                      </div>
                      <div className="font-medium text-[#002B49] mt-1">{m.subject}</div>
                    </div>
                    <div className="text-xs text-[#64748B]">
                      {new Date(m.created_at).toLocaleString()}
                    </div>
                  </div>
                  <p className="text-[#334155] mt-3 leading-relaxed whitespace-pre-wrap">{m.message}</p>
                  {!m.read && (
                    <button
                      onClick={() => markRead(m.id)}
                      className="mt-4 text-xs inline-flex items-center gap-1.5 text-[#002B49] hover:text-[#C5A059] font-medium"
                      data-testid={`mark-read-${i}`}
                    >
                      <MailOpen size={14} /> {t.admin.markRead}
                    </button>
                  )}
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </main>
  );
};

const KpiCard = ({ label, value, Icon, testid }) => (
  <div className="bg-white rounded-2xl border border-black/5 p-6 flex items-start justify-between" data-testid={testid}>
    <div>
      <div className="text-xs uppercase tracking-wider text-[#64748B] font-medium">{label}</div>
      <div className="font-serif text-3xl text-[#002B49] mt-2 font-semibold">{value}</div>
    </div>
    <div className="w-10 h-10 rounded-lg bg-[#FDF8EE] flex items-center justify-center">
      <Icon size={18} className="text-[#C5A059]" />
    </div>
  </div>
);

const TabBtn = ({ active, onClick, children, testid }) => (
  <button
    onClick={onClick}
    className={`inline-flex items-center px-4 py-2 rounded-md text-sm font-medium transition-all ${
      active ? "bg-[#002B49] text-white" : "text-[#64748B] hover:text-[#002B49]"
    }`}
    data-testid={testid}
  >
    {children}
  </button>
);

export default AdminDashboard;
